import {
  Transport,
  CustomerIOTransportConfig,
  TrackingPayload,
  IdentityPayload,
} from '../tracking.types';

const CUSTOMERIO_SCRIPT_ID = 'cio-tracker';

export const createCustomerIOTransport = (): Transport => {
  let _initialized = false;
  let _failed = false;

  const getCIO = (): typeof window._cio | null => {
    if (_failed || !window._cio) return null;
    return window._cio;
  };

  return {
    name: 'customerio',

    init(config: CustomerIOTransportConfig): void {
      if (_initialized || _failed) return;

      if (!config.siteId) {
        console.warn('Customer.io: No Site ID, skipping');
        return;
      }

      try {
        // Initialize queue before script loads
        window._cio = window._cio || ([] as unknown as typeof window._cio);

        // Stub functions that queue calls
        (['identify', 'track'] as const).forEach((fn) => {
          if (!window._cio[fn]) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window._cio as any)[fn] = (...args: unknown[]) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              window._cio.push([fn, ...args] as any);
            };
          }
        });

        const existingScript = document.getElementById(
          CUSTOMERIO_SCRIPT_ID,
        ) as HTMLScriptElement | null;
        if (existingScript) {
          _initialized = true;
          return;
        }

        // Inject script
        const script = document.createElement('script');
        script.async = true;
        script.id = CUSTOMERIO_SCRIPT_ID;
        script.setAttribute('data-site-id', config.siteId);
        script.setAttribute('data-use-array-params', 'true');
        script.src = 'https://assets.customer.io/assets/track.js';
        script.onload = () => {
          _initialized = true;
        };
        script.onerror = () => {
          _failed = true;
        };

        const first = document.getElementsByTagName('script')[0];
        first.parentNode?.insertBefore(script, first);
      } catch {
        _failed = true;
      }
    },

    track(eventName: string, properties: TrackingPayload): void {
      getCIO()?.track(eventName, properties);
    },

    identify(payload: IdentityPayload): void {
      const { userId, email, firstName, lastName, ...rest } = payload;
      getCIO()?.identify({
        id: userId,
        ...rest,
        email,
        first_name: firstName,
        last_name: lastName,
      });
    },
  };
};
